"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const axiosClient_1 = require("./internal/http/axiosClient");
const exportPattern_1 = require("./internal/util/exportPattern");
const strings_1 = require("./internal/util/strings");
class OtaClient {
    /**
     * @param distributionHash hash of released Crowdin project distribution
     * @param config client config
     */
    constructor(distributionHash, config) {
        var _a;
        this.distributionHash = distributionHash;
        this.disableManifestCache = false;
        this.stringsCache = {};
        this.disableStringsCache = false;
        this.languagesCache = null;
        this.disableLanguagesCache = false;
        this.enterpriseOrganizationDomain = null;
        this.disableJsonDeepMerge = false;
        this.apiURL = `https://${this.enterpriseOrganizationDomain ? `${this.enterpriseOrganizationDomain}.` : ''}api.crowdin.com/api/v2/languages?limit=500`;
        this.httpClient = (config === null || config === void 0 ? void 0 : config.httpClient) || new axiosClient_1.AxiosHttpClient();
        this.disableManifestCache = !!(config === null || config === void 0 ? void 0 : config.disableManifestCache);
        this.locale = config === null || config === void 0 ? void 0 : config.languageCode;
        this.disableStringsCache = !!(config === null || config === void 0 ? void 0 : config.disableStringsCache);
        this.disableJsonDeepMerge = !!(config === null || config === void 0 ? void 0 : config.disableJsonDeepMerge);
        this.disableLanguagesCache = !!(config === null || config === void 0 ? void 0 : config.disableLanguagesCache);
        this.enterpriseOrganizationDomain = (_a = config === null || config === void 0 ? void 0 : config.enterpriseOrganizationDomain) !== null && _a !== void 0 ? _a : null;
    }
    /**
     * Distribution hash
     */
    getHash() {
        return this.distributionHash;
    }
    /**
     * Default language code to be used if language was not passed as an input argument of the method
     *
     * @param languageCode laguage code
     */
    setCurrentLocale(languageCode) {
        this.locale = languageCode;
    }
    /**
     * Get language code
     */
    getCurrentLocale() {
        return this.locale;
    }
    /**
     * Get manifest timestamp of distribution
     */
    async getManifestTimestamp() {
        return (await this.manifest).timestamp;
    }
    /**
     * List of files in distribution
     */
    async listFiles() {
        return (await this.manifest).files;
    }
    /**
     * List of project language codes
     */
    async listLanguages() {
        return (await this.manifest).languages;
    }
    /**
     * List of project language codes in the provided format
     * @param format The placeholder format you want to replace your languages with
     */
    async getReplacedLanguages(format) {
        const [projectLanguages, customLanguages, apiLanguages] = await Promise.all([
            this.listLanguages(),
            this.getCustomLanguages(),
            this.getLanguages(),
        ]);
        return projectLanguages.map(l => exportPattern_1.languagePlaceholders[format]((0, exportPattern_1.findLanguageObject)(l, apiLanguages, customLanguages === null || customLanguages === void 0 ? void 0 : customLanguages[l])));
    }
    /**
     * List of files in distribution with variables replaced with the corresponding language code
     */
    async getReplacedFiles() {
        const [customLanguages, languageMappings, files, projectLanguages, apiLanguages] = await Promise.all([
            this.getCustomLanguages(),
            this.getLanguageMappings(),
            this.listFiles(),
            this.listLanguages(),
            this.getLanguages(),
        ]);
        const result = {};
        await Promise.all(projectLanguages.map(async (language) => {
            result[language] = await Promise.all(files.map(file => (0, exportPattern_1.replaceLanguagePlaceholders)(file, language, apiLanguages, languageMappings === null || languageMappings === void 0 ? void 0 : languageMappings[language], customLanguages === null || customLanguages === void 0 ? void 0 : customLanguages[language])));
        }));
        return result;
    }
    /**
     * List of project language objects
     */
    async getLanguageObjects() {
        const [projectLanguages, customLanguages, apiLanguages] = await Promise.all([
            this.listLanguages(),
            this.getCustomLanguages(),
            this.getLanguages(),
        ]);
        return Promise.all(projectLanguages.map(language => (0, exportPattern_1.findLanguageObject)(language, apiLanguages, customLanguages === null || customLanguages === void 0 ? void 0 : customLanguages[language])));
    }
    /**
     * Language mappings
     */
    async getLanguageMappings() {
        const languageMappings = (await this.manifest).language_mapping;
        // languageMappings will be an empty array if there are none, or an object of the provided type otherwise
        return Array.isArray(languageMappings) ? null : languageMappings;
    }
    /**
     * Custom languages
     */
    async getCustomLanguages() {
        const customLanguages = (await this.manifest).custom_languages;
        // customLanguages will be an empty array if there are none, or an object of the provided type otherwise
        return Array.isArray(customLanguages) ? null : customLanguages;
    }
    /**
     * Returns all translations per each language code
     */
    async getTranslations() {
        const languages = await this.listLanguages();
        const translations = {};
        await Promise.all(languages.map(async (language) => {
            translations[language] = await this.getLanguageTranslations(language);
        }));
        return translations;
    }
    /**
     * Returns translations per each file in disribution for specific language
     *
     * @param languageCode language code
     */
    async getLanguageTranslations(languageCode) {
        const language = this.getLanguageCode(languageCode);
        const files = await this.listFiles();
        return Promise.all(files.map(async (file) => {
            const content = await this.getFileTranslations(file, language);
            return { content, file };
        }));
    }
    /**
     * Returns file translations
     *
     * @param file file from distribution
     * @param languageCode language code
     */
    async getFileTranslations(file, languageCode) {
        let url = `${OtaClient.BASE_URL}/${this.distributionHash}/content`;
        const language = this.getLanguageCode(languageCode);
        const [languageMappings, customLanguages, apiLanguages] = await Promise.all([
            this.getLanguageMappings(),
            this.getCustomLanguages(),
            this.getLanguages(),
        ]);
        const languageMapping = (languageMappings !== null && languageMappings !== void 0 ? languageMappings : {})[language];
        const customLanguage = (customLanguages !== null && customLanguages !== void 0 ? customLanguages : {})[language];
        if ((0, exportPattern_1.includesLanguagePlaceholders)(file)) {
            url += (0, exportPattern_1.replaceLanguagePlaceholders)(file, language, apiLanguages, languageMapping, customLanguage);
        }
        else {
            url += `/${language}${file}`;
        }
        const timestamp = await this.getManifestTimestamp();
        url += `?timestamp=${timestamp}`;
        return this.httpClient.get(url).catch(() => null);
    }
    /**
     * Returns translation strings from json-based files for all languages
     *
     * @param file filter strings by specific file (leave undefined to get from all json files)
     */
    async getStrings(file) {
        const files = await this.getJsonFiles(file);
        const languages = await this.listLanguages();
        const res = {};
        await Promise.all(languages.map(async (language) => {
            res[language] = await this.getStringsByFilesAndLocale(files, language);
        }));
        return res;
    }
    /**
     * Returns translation strings from json-based files for specific language
     *
     * @param file filter strings by specific file (leave undefined to get from all json files)
     * @param languageCode language code
     */
    async getStringsByLocale(file, languageCode) {
        const language = this.getLanguageCode(languageCode);
        const files = await this.getJsonFiles(file);
        return this.getStringsByFilesAndLocale(files, language);
    }
    /**
     * Returns translation string for language for specific key
     *
     * @param key path to the translation string in json file
     * @param file filter strings by specific file (leave undefined to get from all json files)
     * @param languageCode language code
     */
    async getStringByKey(key, file, languageCode) {
        const strings = await this.getStringsByLocale(file, languageCode);
        const path = Array.isArray(key) ? key : [key];
        const firstKey = path.shift();
        if (!firstKey) {
            return undefined;
        }
        let res = strings[firstKey];
        for (const keyPart of path) {
            res = res === null || res === void 0 ? void 0 : res[keyPart];
        }
        return res;
    }
    /**
     * Clear cache of translation strings
     */
    clearStringsCache() {
        this.stringsCache = {};
    }
    async getStringsByFilesAndLocale(files, language) {
        var _a;
        let strings = {};
        for (const filePath of files) {
            let content;
            const fileCache = await ((_a = this.stringsCache[filePath]) !== null && _a !== void 0 ? _a : {})[language];
            if (fileCache) {
                content = fileCache;
            }
            else {
                if (!this.disableStringsCache) {
                    this.stringsCache[filePath] = {
                        ...this.stringsCache[filePath],
                        [language]: this.getFileTranslations(filePath, language),
                    };
                }
                content = await this.stringsCache[filePath][language];
            }
            if (this.disableJsonDeepMerge) {
                strings = { ...strings, ...content };
            }
            else {
                (0, strings_1.mergeDeep)(strings, content);
            }
        }
        return strings;
    }
    get manifest() {
        if (this.manifestHolder && !this.disableManifestCache) {
            return this.manifestHolder;
        }
        else {
            this.manifestHolder = this.httpClient.get(`${OtaClient.BASE_URL}/${this.distributionHash}/manifest.json`);
            return this.manifestHolder;
        }
    }
    getLanguageCode(lang) {
        const languageCode = lang || this.getCurrentLocale();
        if (languageCode) {
            return languageCode;
        }
        else {
            throw new Error('Language code should be either provided through input arguments or by "setCurrentLocale" method');
        }
    }
    async getJsonFiles(file) {
        return (await this.listFiles()).filter(f => !file || file === f).filter(strings_1.isJsonFile);
    }
    getLanguages() {
        if (this.languagesCache) {
            return this.languagesCache;
        }
        const languages = this.httpClient.get(this.apiURL).then(d => d.data.map(l => l.data));
        if (!this.disableLanguagesCache) {
            this.languagesCache = languages;
        }
        return languages;
    }
}
exports.default = OtaClient;
OtaClient.BASE_URL = 'https://distributions.crowdin.net';
